import {IInputController} from "@viscircle-org/threngine/dist/types/src/core/render/input/controller/IInputController";
import { Vector2, MOUSE } from "three";
import {EventSource, IEvent} from "@viscircle-org/threngine";

enum InputType {
    Mouse,
    Touch
}

enum State {
    NONE = -1,
    ROTATE = 0,
    DOLLY = 1,
    PAN = 2,
}

export class CustomInputSimple implements IInputController{
    private state = State.NONE;

    private rotateStart:Vector2;
    private rotateEnd:Vector2;
    private rotateDelta:Vector2;

    private panStart:Vector2;
    private panEnd:Vector2;
    private panDelta:Vector2;

    private dollyStart:Vector2;
    private dollyEnd:Vector2;
    private dollyDelta:Vector2;

    private domElement:HTMLElement;

    public rotateSource:EventSource<{type:InputType, theta:number, phi:number}> = new EventSource();
    public panSource:EventSource<{type: InputType, vector: Vector2}> = new EventSource();
    public zoomSource:EventSource<{type:InputType, delta:number}> = new EventSource();
    public zoomSourceTwo:EventSource<{event:any}> = new EventSource();

    public onRotate:IEvent<{type:InputType, theta:number, phi:number}> = this.rotateSource;
    public onPan:IEvent<{type: InputType, vector: Vector2}> = this.panSource;
    public onZoom:IEvent<{type:InputType, delta:number}> = this.zoomSource;
    public onZoomTwo:IEvent<{event:any}> = this.zoomSourceTwo;

    private panInfo:any;

    constructor(domElement:HTMLElement) {
        this.state = State.NONE;
        this.rotateStart = new Vector2();
        this.rotateEnd = new Vector2();
        this.rotateDelta = new Vector2();
        this.panStart = new Vector2();
        this.panEnd = new Vector2();
        this.panDelta = new Vector2();
        this.dollyStart = new Vector2();
        this.dollyEnd = new Vector2();
        this.dollyDelta = new Vector2();
        this.domElement = domElement;

        this.domElement.addEventListener( 'mousedown', this.onMouseDown, false );
        this.domElement.addEventListener( 'touchstart', this._onTouchDown, false );
        this.domElement.addEventListener( 'wheel', this._onMouseWheel );

        //  We ignore all touch actions on our canvas element
        this.domElement.style.touchAction = "none";
    }

    public rebind = () => {
        this.domElement.addEventListener( 'mousedown', this.onMouseDown, false );
        this.domElement.addEventListener( 'touchstart', this._onTouchDown, false );
        this.domElement.addEventListener( 'wheel', this._onMouseWheel );

        //  We ignore all touch actions on our canvas element
        this.domElement.style.touchAction = "none";
    }

    dispose = () => {
        this.domElement.removeEventListener( 'mousedown', this.onMouseDown, false );
        this.domElement.removeEventListener( 'touchstart', this._onTouchDown, false );
        this.domElement.removeEventListener( 'wheel', this._onMouseWheel, false );

        window.removeEventListener( 'mousemove', this._onMouseMove, false );
        window.removeEventListener( 'mouseup', this._onMouseUp, false );

        window.removeEventListener( 'touchmove', this._onTouchMove, false );
        window.removeEventListener( 'touchend', this._onTouchUp, false );
    };

    private _rotateActive:boolean = true;
    private _dollyActive:boolean = true;
    private _panActive:boolean = true;

    public set rotateActive(value:boolean) {
        this._rotateActive = value;
    }

    public set dollyActive(value:boolean) {
        this._dollyActive = value;
    }

    public set panActive(value:boolean) {
        this._panActive = value;
    }

    private _rotate = (x:number, y:number, type:InputType) => {
        this.rotateEnd.set(x, y);
        this.rotateDelta.subVectors( this.rotateEnd, this.rotateStart );
        this.rotateStart.copy( this.rotateEnd );

        // Horizontal and vertical rotate speed is determined by height
        const factor = 2 * Math.PI / this.domElement.clientHeight;

        // rotating across whole screen goes 360 degrees around
        const theta = factor * this.rotateDelta.x;

        // rotating up and down along whole screen attempts to go 360, but limited to 180
        const phi = factor * this.rotateDelta.y;

        this.rotateSource.emit({type, theta, phi});
    }

    private _zoom = (x:number, y:number, type:InputType) => {
        this.dollyEnd.set(x, y);
        this.dollyDelta.subVectors( this.dollyEnd, this.dollyStart );
        this.dollyStart.copy( this.dollyEnd );

        let delta = this.dollyDelta.y
        if (type == InputType.Touch) delta *= -1;

        this.zoomSource.emit({type: type, delta: delta});
    }

    private _pan = (x:number, y:number, type:InputType) => {
        this.panEnd.set(x, y);
        this.panDelta.subVectors( this.panEnd, this.panStart );
        this.panStart.copy( this.panEnd );

        let panx = this.panDelta.x;
        let pany = this.panDelta.y;

        this.panSource.emit( {type: type, vector: new Vector2(panx, pany)} );
    }

    private onMouseDown = (event:MouseEvent) => {
        // THIS IS EXPLICITELY NEEDED TO PREVENT STRANGE STICKY
        // SITUATIONS WITH OTHER DOM ELEMENTS!
        event.preventDefault();

        switch (event.button) {

            case MOUSE.RIGHT:
                this.panStart.set( event.clientX, event.clientY );
                this.state = State.PAN;
                break;

            case MOUSE.MIDDLE:
                this.dollyStart.set( event.clientX, event.clientY );
                this.state = State.DOLLY;
                break;

            case MOUSE.LEFT:
                this.rotateStart.set( event.clientX, event.clientY );
                this.state = State.ROTATE;
                break;
        }

        if (this.state !== State.NONE) {
            // Make sure to register on the window instead of document
            // This way you prevent "sticky buttons". This would happen
            // when the mouse up happens on a different dom element than
            // the one the movement started.
            window.addEventListener( 'mousemove', this._onMouseMove, false );
            window.addEventListener( 'mouseup', this._onMouseUp, false );
        }
    }

    private _onMouseMove = (event:MouseEvent) => {
        switch (this.state) {
            case State.ROTATE:
                if (!this._rotateActive) break;
                this._rotate(event.clientX, event.clientY, InputType.Mouse);
                break;

            case State.DOLLY:
                if (!this._dollyActive) break;
                this._zoom(event.clientX, event.clientY, InputType.Mouse);
                break;

            case State.PAN:
                if (!this._panActive) break;
                this._pan(event.clientX, event.clientY, InputType.Mouse);
                break;
        }
    }

    private _onMouseUp = (event:any) => {
        this.state = State.NONE;
        window.removeEventListener( 'mousemove', this._onMouseMove, false );
        window.removeEventListener( 'mouseup', this._onMouseUp, false );
    }

    private _onTouchDown = (event:TouchEvent) => {
        switch (event.touches.length) {
            case 1:	// one-fingered touch: rotate
                if (!this._rotateActive) break;
                this.rotateStart.set(event.touches[0].pageX, event.touches[0].pageY);
                this.state = State.ROTATE;
                break;

            case 2:	// two-fingered touch: dolly
                if (!this._dollyActive) break;
                const cords = this._toTouchZoomCoords(event);
                this.dollyStart.set(cords.x, cords.y);
                this.state = State.DOLLY;
                break;

            case 3: // three-fingered touch: pan
                if (!this._panActive) break; 
                this.panStart.set( event.touches[0].pageX, event.touches[0].pageY)
                this.state = State.PAN;
                break;
        }

        if (this.state !== State.NONE) {
            // Make sure to register on the window instead of document
            // This way you prevent "sticky buttons". This would happen
            // when the mouse up happens on a different dom element than
            // the one the movement started.
            window.addEventListener( 'touchmove', this._onTouchMove, false );
            window.addEventListener( 'touchend', this._onTouchUp, false );
        }
    }

    private _onTouchMove = (event:TouchEvent) => {
        switch (event.touches.length) {
            case 1: // one-fingered touch: rotate
                if (!this._rotateActive) break;
                this._rotate(event.touches[0].pageX, event.touches[0].pageY, InputType.Touch);
                break;

            case 2: // two-fingered touch: dolly
                if (!this._dollyActive) break;
                const cords = this._toTouchZoomCoords(event);
                this._zoom(cords.x, cords.y, InputType.Touch);
                break;

            case 3: // three-fingered touch: pan
                if (!this._panActive) break;
                this._pan(event.touches[0].pageX, event.touches[0].pageY, InputType.Touch);
                break;
        }
    }

    private _onTouchUp = (event:TouchEvent) => {
        this.state = State.NONE;
        window.removeEventListener( 'touchmove', this._onTouchMove, false );
        window.removeEventListener( 'touchend', this._onTouchUp, false );
    }

    private _onMouseWheel = (event:any) => {
        if (!this._dollyActive) return;
        if (this.state !== State.NONE && this.state !== State.ROTATE) return;

        event.preventDefault();
        event.stopPropagation();

        this.zoomSource.emit( {type: InputType.Mouse, delta: event.deltaY} );
        this.zoomSourceTwo.emit( {event:event} );
    }

    private _toTouchZoomCoords = (event:TouchEvent):{x:number, y:number} => {
        const dx = event.touches[ 0 ].pageX - event.touches[ 1 ].pageX;
        const dy = event.touches[ 0 ].pageY - event.touches[ 1 ].pageY;
        const distance = Math.sqrt( dx * dx + dy * dy );
        return {x: 0, y: distance};
    }

}
